import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import SEO from '../../components/common/SEO';
import { SpinningBenzene } from '../../components/common/PharmaAnimations';

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'impurity_standard', label: 'Impurity Standard' },
  { value: 'reference_standard', label: 'Reference Standard' },
  { value: 'metabolite', label: 'Metabolite' },
  { value: 'degradation_product', label: 'Degradation Product' },
  { value: 'synthesis_intermediate', label: 'Synthesis Intermediate' },
  { value: 'custom', label: 'Custom' },
];

const STOCK_OPTIONS = [
  { value: '', label: 'All Stock' },
  { value: 'in_stock', label: 'In Stock' },
  { value: 'on_request', label: 'On Request' },
];

function StockBadge({ status }) {
  const inStock = status === 'in_stock';
  return (
    <span style={{
      fontSize: '0.7rem', fontWeight: 600, padding: '3px 9px', borderRadius: 20,
      background: inStock ? '#d1fae5' : '#fef3c7',
      color: inStock ? '#059669' : '#d97706',
    }}>
      {inStock ? 'In Stock' : 'On Request'}
    </span>
  );
}

function CatBadge({ category }) {
  const isImp = category === 'impurity';
  return (
    <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '3px 9px', borderRadius: 20, background: isImp ? '#eff6ff' : '#f0fdf4', color: isImp ? '#1d4ed8' : '#16a34a' }}>
      {isImp ? 'Impurity' : 'Ref. Standard'}
    </span>
  );
}

// Product image — shows real PNG if available, falls back to styled SVG placeholder
function ProductImage({ item, height = 100 }) {
  const [imgFailed, setImgFailed] = React.useState(false);
  const category = item?.category || '';
  const isImp = category === 'impurity';
  const bg1 = isImp ? '#eff6ff' : '#f0fdf4';
  const bg2 = isImp ? '#dbeafe' : '#dcfce7';
  const accent = isImp ? '#1d4ed8' : '#16a34a';
  const label = isImp ? 'IMP' : 'RS';

  if (item?.image && !imgFailed) {
    return (
      <div style={{ width: '100%', height, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        <img
          src={`/products/${item.image}`}
          alt={item.name}
          style={{ maxWidth: '100%', maxHeight: height, objectFit: 'contain', padding: 8 }}
          onError={() => setImgFailed(true)}
        />
      </div>
    );
  }

  return (
    <div style={{
      width: '100%', height,
      background: `linear-gradient(135deg, ${bg1}, ${bg2})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      position: 'relative', overflow: 'hidden',
    }}>
      <svg width="120" height="80" viewBox="0 0 120 80" style={{ position: 'absolute', opacity: 0.1 }}>
        <circle cx="24" cy="40" r="12" stroke={accent} strokeWidth="2" fill="none" />
        <circle cx="60" cy="18" r="12" stroke={accent} strokeWidth="2" fill="none" />
        <circle cx="96" cy="40" r="12" stroke={accent} strokeWidth="2" fill="none" />
        <circle cx="60" cy="62" r="12" stroke={accent} strokeWidth="2" fill="none" />
        <line x1="36" y1="36" x2="48" y2="24" stroke={accent} strokeWidth="1.5" />
        <line x1="72" y1="24" x2="84" y2="36" stroke={accent} strokeWidth="1.5" />
        <line x1="84" y1="44" x2="72" y2="56" stroke={accent} strokeWidth="1.5" />
        <line x1="48" y1="56" x2="36" y2="44" stroke={accent} strokeWidth="1.5" />
      </svg>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: accent, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 800, fontSize: '0.75rem',
        boxShadow: `0 4px 12px ${accent}66`,
        position: 'relative', zIndex: 1,
      }}>{label}</div>
    </div>
  );
}



export default function PublicCatalog() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({ search: '', category: '', stock_status: '' });
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [shortlist, setShortlist] = useState([]); // array of item ids
  const [showShortlistQuote, setShowShortlistQuote] = useState(false);
  const [slForm, setSlForm] = useState({ name: '', email: '', message: '' });
  const [slSubmitting, setSlSubmitting] = useState(false);
  const [slSent, setSlSent] = useState(false);
  const debounceRef = React.useRef(null);
  const LIMIT = 12;

  const load = useCallback(async (p, f) => {
    setLoading(true);
    try {
      const { data } = await api.get('/public/catalog', { params: { ...f, page: p, limit: LIMIT } });
      setItems(data.data);
      setTotal(data.total);
    } catch (_) { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(page, filters); }, [page, filters, load]);

  // Read ?search= from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get('search') || '';
    if (s) { setSearch(s); setFilters(f => ({ ...f, search: s })); }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setFilters(f => ({ ...f, search }));
  };

  // Instant search with debounce
  const handleSearchInput = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setFilters(f => ({ ...f, search: val }));
    }, 400);
  };

  const toggleShortlist = (e, item) => {
    e.stopPropagation();
    setShortlist(sl => sl.includes(item.id) ? sl.filter(i => i !== item.id) : [...sl, item]);
  };

  const isShortlisted = (id) => shortlist.some(i => (typeof i === 'object' ? i.id : i) === id);

  const handleShortlistQuote = async (e) => {
    e.preventDefault();
    if (!slForm.name || !slForm.email) return;
    setSlSubmitting(true);
    try {
      const productList = shortlist.map(i => `${i.name} (${i.catalog_number || ''})`).join('\n');
      await api.post('/public/contact', {
        name: slForm.name, email: slForm.email,
        subject: `Shortlist Quote Request — ${shortlist.length} items`,
        message: `Requesting quotes for the following items:\n${productList}\n\n${slForm.message}`,
        product_interest: shortlist.map(i => i.catalog_number).filter(Boolean).join(', '),
      });
      setSlSent(true);
    } catch { /* silent */ }
    finally { setSlSubmitting(false); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <SEO
        title="Pharmaceutical Reference Standards & Drug Impurities Catalog"
        description="Browse PRASI LABS' certified catalog of pharmaceutical reference standards, drug impurity standards, metabolites, degradation products, and stable isotopes. USP, EP, BP, ICH grades. Request a quote online."
        keywords="pharmaceutical reference standards catalog, drug impurity standards buy, impurity standards list, metabolites supplier India, degradation products, stable isotopes catalog, USP EP BP reference standards"
        canonical="/catalog"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Pharmaceutical Reference Standards & Drug Impurities Catalog',
          description: 'Certified pharmaceutical reference standards, drug impurities, metabolites and stable isotopes from PRASI LABS.',
          url: 'https://www.prasilabs.com/catalog',
          publisher: { '@id': 'https://www.prasilabs.com/#organization' },
        }}
      />
      <div>
        <style>{`
        @media (max-width: 768px) {
          .pub-catalog-hero { padding: 44px 16px 36px !important; }
          .pub-catalog-wrap { padding: 20px 14px !important; }
          .pub-filter-bar { flex-direction: column !important; }
          .pub-filter-bar form, .pub-filter-bar select { width: 100% !important; box-sizing: border-box; }
          .pub-filter-bar .pub-view-toggle { align-self: flex-end; }
          .pub-shortlist-bar > div { flex-wrap: wrap; gap: 8px !important; border-radius: 16px !important; padding: 12px 16px !important; }
        }
        @media (max-width: 480px) {
          .pub-catalog-hero h1 { font-size: 1.7rem !important; }
        }
      `}</style>

        {/* Page hero */}
        <div className="pub-catalog-hero" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1e3a8a 55%, #1d4ed8 100%)', padding: '64px 24px 58px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <SpinningBenzene size={160} color="white" style={{ position: 'absolute', right: 60, top: '50%', transform: 'translateY(-50%)', opacity: 0.1, pointerEvents: 'none' }} />
          <svg style={{ position: 'absolute', left: 60, top: '50%', transform: 'translateY(-50%)', opacity: 0.05 }} width="220" height="190" viewBox="0 0 220 190">
            <circle cx="55" cy="95" r="28" stroke="white" strokeWidth="2.5" fill="none" />
            <circle cx="110" cy="42" r="28" stroke="white" strokeWidth="2.5" fill="none" />
            <circle cx="165" cy="95" r="28" stroke="white" strokeWidth="2.5" fill="none" />
            <circle cx="110" cy="148" r="28" stroke="white" strokeWidth="2.5" fill="none" />
            <line x1="80" y1="77" x2="92" y2="60" stroke="white" strokeWidth="2" />
            <line x1="128" y1="60" x2="140" y2="77" stroke="white" strokeWidth="2" />
            <line x1="140" y1="113" x2="128" y2="130" stroke="white" strokeWidth="2" />
            <line x1="92" y1="130" x2="80" y2="113" stroke="white" strokeWidth="2" />
          </svg>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 20, padding: '4px 16px', fontSize: '0.72rem', fontWeight: 700, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 18 }}>
              Pharmaceutical Standards
            </div>
            <h1 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 900, color: '#fff', marginBottom: 14, letterSpacing: '-0.5px' }}>Product Catalog</h1>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '1rem', maxWidth: 480, margin: '0 auto', lineHeight: 1.75 }}>
              Browse our pharmaceutical drug impurities and reference standards. Contact us for pricing and availability.
            </p>
          </div>
        </div>

        {/* Shortlist floating bar */}
        {shortlist.length > 0 && !showShortlistQuote && (
          <div className="pub-shortlist-bar">
            <div style={{ background: '#1d4ed8', color: '#fff', borderRadius: 30, padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 8px 32px rgba(29,78,216,0.45)', whiteSpace: 'nowrap' }}>
              <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>📋 {shortlist.length} item{shortlist.length !== 1 ? 's' : ''} shortlisted</span>
              <button onClick={() => setShowShortlistQuote(true)} style={{ padding: '8px 18px', background: '#fff', color: '#1d4ed8', border: 'none', borderRadius: 20, fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer' }}>
                Request Bulk Quote →
              </button>
              <button onClick={() => setShortlist([])} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '50%', width: 26, height: 26, fontWeight: 700, fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
          </div>
        )}

        {/* Shortlist quote modal */}
        {showShortlistQuote && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setShowShortlistQuote(false)}>
            <div style={{ background: '#fff', borderRadius: 18, maxWidth: 520, width: '100%', padding: '32px', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
              <div style={{ fontWeight: 900, fontSize: '1.15rem', color: '#0f172a', marginBottom: 6 }}>Request Bulk Quote</div>
              <p style={{ fontSize: '0.84rem', color: '#64748b', marginBottom: 18 }}>Requesting quotes for {shortlist.length} shortlisted items.</p>
              {/* Item list */}
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px', marginBottom: 20, maxHeight: 140, overflowY: 'auto' }}>
                {shortlist.map(item => (
                  <div key={item.id} style={{ fontSize: '0.8rem', color: '#374151', padding: '4px 0', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{item.name}</span>
                    <span style={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: '0.73rem' }}>{item.catalog_number}</span>
                  </div>
                ))}
              </div>
              {slSent ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>✅</div>
                  <div style={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', marginBottom: 6 }}>Quote Request Sent!</div>
                  <div style={{ fontSize: '0.85rem', color: '#64748b' }}>We'll respond within one business day.</div>
                  <button onClick={() => { setShowShortlistQuote(false); setSlSent(false); setShortlist([]); }} style={{ marginTop: 16, padding: '10px 24px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 9, fontWeight: 700, cursor: 'pointer' }}>Done</button>
                </div>
              ) : (
                <form onSubmit={handleShortlistQuote} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <input required placeholder="Your Name *" value={slForm.name} onChange={e => setSlForm(f => ({ ...f, name: e.target.value }))} style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.88rem', outline: 'none' }} />
                  <input required type="email" placeholder="Email Address *" value={slForm.email} onChange={e => setSlForm(f => ({ ...f, email: e.target.value }))} style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.88rem', outline: 'none' }} />
                  <textarea placeholder="Additional notes (quantities, deadlines, etc.)" value={slForm.message} onChange={e => setSlForm(f => ({ ...f, message: e.target.value }))} rows={3} style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.88rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="button" onClick={() => setShowShortlistQuote(false)} style={{ flex: 1, padding: '11px', borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' }}>Cancel</button>
                    <button type="submit" disabled={slSubmitting} style={{ flex: 2, padding: '11px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 9, fontWeight: 700, cursor: slSubmitting ? 'not-allowed' : 'pointer', fontSize: '0.88rem', opacity: slSubmitting ? 0.7 : 1 }}>
                      {slSubmitting ? 'Sending…' : 'Send Quote Request →'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        <div className="pub-catalog-wrap" style={{ maxWidth: 1200, margin: '0 auto', padding: '36px 24px' }}>
          {/* Filters */}
          <div className="pub-filter-bar" style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '18px 20px', marginBottom: 28, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <form onSubmit={handleSearch} style={{ flex: 1, minWidth: 200, display: 'flex', gap: 8 }}>
              <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center' }}>
                <svg width="15" height="15" fill="none" stroke="#94a3b8" strokeWidth="2" viewBox="0 0 24 24" style={{ position: 'absolute', left: 10, pointerEvents: 'none' }}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <input
                  value={search}
                  onChange={e => handleSearchInput(e.target.value)}
                  placeholder="Search by name, catalog #, CAS…"
                  style={{ width: '100%', padding: '12px 14px 12px 32px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                />
                {search && <button type="button" onClick={() => handleSearchInput('')} style={{ position: 'absolute', right: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1rem', lineHeight: 1, padding: 2 }}>✕</button>}
              </div>
            </form>
            <select value={filters.category}
              onChange={e => { setPage(1); setFilters(f => ({ ...f, category: e.target.value })); }}
              style={{ padding: '12px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.9rem', background: '#fff', cursor: 'pointer', outline: 'none' }}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <select value={filters.stock_status}
              onChange={e => { setPage(1); setFilters(f => ({ ...f, stock_status: e.target.value })); }}
              style={{ padding: '12px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.9rem', background: '#fff', cursor: 'pointer', outline: 'none' }}>
              {STOCK_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <div style={{ fontSize: '0.82rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>{total} item{total !== 1 ? 's' : ''}</div>
            {/* View toggle */}
            <div className="pub-view-toggle" style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 8, padding: 3 }}>
              {[['grid', (
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>
              )], ['list', (
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
              )]].map(([mode, icon]) => (
                <button key={mode} onClick={() => setViewMode(mode)}
                  style={{ padding: '5px 9px', borderRadius: 6, border: 'none', cursor: 'pointer', background: viewMode === mode ? '#fff' : 'transparent', color: viewMode === mode ? '#1d4ed8' : '#94a3b8', boxShadow: viewMode === mode ? '0 1px 4px rgba(0,0,0,0.08)' : 'none', display: 'flex', alignItems: 'center', transition: 'all 0.15s' }}>
                  {icon}
                </button>
              ))}
            </div>
          </div>

          {/* Grid / List */}
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
              <div className="spinner spinner-lg" />
            </div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>No items found. Try adjusting your search.</div>
          ) : (() => {
            const groups = {};
            const ungrouped = [];
            for (const item of items) {
              if (item.parent_product) {
                if (!groups[item.parent_product]) groups[item.parent_product] = [];
                groups[item.parent_product].push(item);
              } else {
                ungrouped.push(item);
              }
            }
            const hasGroups = Object.keys(groups).length > 0;

            const ShortlistBtn = ({ item }) => {
              const inList = isShortlisted(item.id);
              return (
                <button
                  onClick={e => toggleShortlist(e, item)}
                  title={inList ? 'Remove from shortlist' : 'Add to shortlist'}
                  style={{ background: inList ? '#eff6ff' : '#f8fafc', border: `1px solid ${inList ? '#1d4ed8' : '#e2e8f0'}`, borderRadius: 7, padding: '5px 8px', cursor: 'pointer', color: inList ? '#1d4ed8' : '#94a3b8', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', fontWeight: 700, transition: 'all 0.15s', flexShrink: 0 }}>
                  {inList ? '★' : '☆'}
                </button>
              );
            };

            const GridCard = ({ item }) => (
              <div
                style={{ background: '#fff', borderRadius: 14, border: `1px solid ${isShortlisted(item.id) ? '#1d4ed8' : '#e2e8f0'}`, overflow: 'hidden', cursor: 'pointer', transition: 'box-shadow 0.15s, transform 0.15s', boxShadow: isShortlisted(item.id) ? '0 0 0 2px #1d4ed820' : '0 1px 4px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(29,78,216,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = isShortlisted(item.id) ? '0 0 0 2px #1d4ed820' : '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'none'; }}
                onClick={() => navigate(`/catalog/${item.id}`)}>
                <ProductImage item={item} height={120} />
                <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', lineHeight: 1.4 }}>{item.name}</div>
                    <StockBadge status={item.stock_status} />
                  </div>
                  {item.catalog_number && <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontFamily: 'monospace' }}>{item.catalog_number}</div>}
                  {item.cas_number && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>CAS: {item.cas_number}</div>}
                  {item.purity && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Purity: {item.purity}</div>}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
                    <CatBadge category={item.category} />
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <ShortlistBtn item={item} />
                      <span style={{ fontSize: '0.75rem', color: '#1d4ed8', fontWeight: 700 }}>Details →</span>
                    </div>
                  </div>
                </div>
              </div>
            );

            const ListRow = ({ item }) => (
              <div
                style={{ background: '#fff', borderRadius: 12, border: `1px solid ${isShortlisted(item.id) ? '#1d4ed8' : '#e2e8f0'}`, padding: '14px 18px', cursor: 'pointer', transition: 'box-shadow 0.15s', display: 'flex', gap: 16, alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(29,78,216,0.1)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'}
                onClick={() => navigate(`/catalog/${item.id}`)}>
                <div style={{ width: 52, height: 52, flexShrink: 0, borderRadius: 10, overflow: 'hidden' }}>
                  <ProductImage item={item} height={52} />
                </div>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 200, flex: 2 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                    <div style={{ fontSize: '0.73rem', color: '#94a3b8', fontFamily: 'monospace' }}>{item.catalog_number}</div>
                  </div>
                  {item.cas_number && <div style={{ fontSize: '0.78rem', color: '#64748b', flexShrink: 0 }}>CAS: <strong>{item.cas_number}</strong></div>}
                  {item.purity && <div style={{ fontSize: '0.78rem', color: '#64748b', flexShrink: 0 }}>Purity: <strong>{item.purity}</strong></div>}
                  <CatBadge category={item.category} />
                  <StockBadge status={item.stock_status} />
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                  <ShortlistBtn item={item} />
                  <span style={{ fontSize: '0.8rem', color: '#1d4ed8', fontWeight: 700, whiteSpace: 'nowrap' }}>Details →</span>
                </div>
              </div>
            );

            const renderItems = (list) => viewMode === 'list'
              ? <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{list.map(item => <ListRow key={item.id} item={item} />)}</div>
              : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>{list.map(item => <GridCard key={item.id} item={item} />)}</div>;

            return (
              <div>
                {Object.entries(groups).map(([parent, groupItems]) => (
                  <div key={parent} style={{ marginBottom: 36 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                      <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                      <div style={{ background: 'linear-gradient(135deg, #1e3a8a, #1d4ed8)', color: '#fff', padding: '6px 18px', borderRadius: 20, fontWeight: 700, fontSize: '0.875rem', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(29,78,216,0.2)' }}>
                        {parent} <span style={{ opacity: 0.7, fontSize: '0.75rem', fontWeight: 400 }}>· {groupItems.length} standard{groupItems.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                    </div>
                    {renderItems(groupItems)}
                  </div>
                ))}
                {ungrouped.length > 0 && (
                  <div>
                    {hasGroups && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                        <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                        <div style={{ color: '#64748b', fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap' }}>Other Standards</div>
                        <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                      </div>
                    )}
                    {renderItems(ungrouped)}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 40 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', color: page === 1 ? '#cbd5e1' : '#1d4ed8', fontWeight: 600, fontSize: '0.875rem' }}>
                ← Prev
              </button>
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 3, totalPages - 6));
                const pg = start + i;
                return (
                  <button key={pg} onClick={() => setPage(pg)}
                    style={{
                      padding: '8px 14px', borderRadius: 8, border: '1px solid', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
                      borderColor: page === pg ? '#1d4ed8' : '#e2e8f0',
                      background: page === pg ? '#1d4ed8' : '#fff',
                      color: page === pg ? '#fff' : '#475569'
                    }}>
                    {pg}
                  </button>
                );
              })}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer', color: page === totalPages ? '#cbd5e1' : '#1d4ed8', fontWeight: 600, fontSize: '0.875rem' }}>
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
